/**
 * A state's answer for a tax category (piece 25; decisions 0901, 0902, 0904).
 *
 * 🔒 A `TaxCategoryStateRule` row answers with one of four treatments, the one
 * list every state answers from (the product owner, 2026-09-25: *"I want this as
 * a universal setup"*). NotTaxable charges nothing; Taxable charges the state's
 * rate; Custom charges its own `ratePercent` in place of it; Default charges as
 * Taxable does and keeps the pairing on the review report until somebody
 * confirms it. 🔨 A rate stands on a Custom row and on no other. A `note` is
 * optional on every treatment.
 *
 * 🔒 Every state's thirteen answers are seeded from the committed research,
 * `docs/reference/sales-tax-rates/category-answers.json`, as a starting point
 * on decision 0646's rules: the seed never writes a pairing that already
 * carries a row, and the screen marks a seeded row until a person answers over
 * it, read off its source.
 */
import { NO_SALES_TAX_STATES, US_STATE_NAMES } from '../records/taxExemption';
import { t } from '../strings';
import { formatPercent } from '../money';
import { TAX_CATEGORY_ROWS } from '../catalog/taxCategories';
import { ratePercentProblem } from './rateTable';

/** The four treatments a rule row gives, in the order the screen offers them (decision 0904). */
export const TAX_TREATMENTS = ['NotTaxable', 'Default', 'Taxable', 'Custom'] as const;
export type TaxTreatmentValue = (typeof TAX_TREATMENTS)[number];

/** What the four treatments are called on the screen. */
export const TAX_TREATMENT_LABELS: Readonly<Record<TaxTreatmentValue, string>> = {
  NotTaxable: t('tax.treatment.notTaxable'),
  Default: t('tax.treatment.default'),
  Taxable: t('tax.treatment.taxable'),
  Custom: t('tax.treatment.custom'),
};

/** A rule row's answer as its three columns hold it. */
export interface TaxTreatmentShape {
  treatment: TaxTreatmentValue;
  note: string | null;
  ratePercent: number | null;
}

export function isTaxTreatment(value: unknown): value is TaxTreatmentValue {
  return typeof value === 'string' && (TAX_TREATMENTS as readonly string[]).includes(value);
}

/**
 * 🔨 The one refusal of a malformed answer, read by the rule route before the
 * database's own check constraint would refuse it, and by the editor before
 * Save: one of the four treatments; a rate on Custom and on no other, one the
 * rate table itself would take; and a note, where there is one, that is words.
 */
export function taxTreatmentProblem(input: { treatment?: unknown; note?: unknown; ratePercent?: unknown }): string | null {
  if (!isTaxTreatment(input.treatment)) return `a rule row's treatment is one of ${TAX_TREATMENTS.join(', ')}`;
  const rate = input.ratePercent === undefined || input.ratePercent === null || input.ratePercent === '' ? null : input.ratePercent;
  if (input.note !== undefined && input.note !== null && typeof input.note !== 'string') return 'a note is words';
  if (input.treatment === 'Custom') {
    if (rate === null) return 'a Custom treatment names its rate';
    const problem = ratePercentProblem(rate);
    if (problem) return `the Custom rate: ${problem}`;
    return null;
  }
  if (rate !== null) return 'only a Custom treatment carries a rate';
  return null;
}

/** A rate as the answer reads it: 1, 6.25 and 1.225 rather than 1.000. */
function answerRate(ratePercent: number): string {
  return formatPercent(ratePercent, { maxDecimals: 4 });
}

/**
 * 🔒 A treatment as the Sales tax page's answer column reads it (decision 0904):
 * *Not taxable*, *Taxable*, *Custom 1%*, or *Default · review*, the review word
 * saying the pairing is on the review report. A row's note is drawn beneath its
 * line, so every line's treatment stands in one place.
 */
export function taxTreatmentWord(row: Pick<TaxTreatmentShape, 'treatment' | 'ratePercent'>): string {
  if (row.treatment === 'Custom' && row.ratePercent !== null) return `${TAX_TREATMENT_LABELS.Custom} ${answerRate(row.ratePercent)}`;
  if (row.treatment === 'Default') return `${TAX_TREATMENT_LABELS.Default} · ${t('tax.treatment.review')}`;
  return TAX_TREATMENT_LABELS[row.treatment];
}

/** A treatment as the audit history reads it: the column's word, then the note where there is one. */
export function taxTreatmentLabel(row: TaxTreatmentShape): string {
  const word = row.treatment === 'Custom' && row.ratePercent !== null ? `${TAX_TREATMENT_LABELS.Custom} ${answerRate(row.ratePercent)}` : TAX_TREATMENT_LABELS[row.treatment];
  const note = row.note?.trim();
  return note ? `${word}: ${note}` : word;
}

// ---- the seeded answers (decision 0902) ----

/** The day every seeded series starts: the research reads each state's rule as it stood from 2025-01-01. */
export const SEEDED_RULE_AS_OF = '2025-01-01';

/** The day the research read each state's page, which every seeded row names as last verified. */
export const SEEDED_RULE_VERIFIED_ON = '2026-09-25';

const SEEDED_RULE_LEAD = 'Seeded starting point from ';
const SEEDED_RULE_TAIL = '; confirm against the state before filing.';

/**
 * 🔒 The source a seeded row carries, naming the state's own page (decision
 * 0902). It is also how the screen tells a seeded row from one a person
 * entered, so the mark needs no column of its own.
 */
export function seededRuleSource(url: string): string {
  return `${SEEDED_RULE_LEAD}${url}${SEEDED_RULE_TAIL}`;
}

export function isSeededRuleSource(source: string | null | undefined): boolean {
  return typeof source === 'string' && source.startsWith(SEEDED_RULE_LEAD) && source.endsWith(SEEDED_RULE_TAIL);
}

/** The first web address a source names, which the screen draws as its link; null where it names none. */
export function sourceUrlOf(source: string | null | undefined): string | null {
  const match = /https?:\/\/\S+/.exec(source ?? '');
  if (!match) return null;
  // A page's own address may hold parentheses, as Connecticut's ip-2018(2).pdf does; only punctuation after it, or a closing one it never opened, is cut.
  let url = match[0].replace(/[.;,]+$/, '');
  while (url.endsWith(')') && (url.match(/\(/g) ?? []).length < (url.match(/\)/g) ?? []).length) url = url.slice(0, -1).replace(/[.;,]+$/, '');
  return url;
}

/** The research's four answers, as the committed file spells them. */
export const RESEARCH_ANSWERS = ['Taxed', 'Not taxed', 'Conditional', 'Lower rate'] as const;
export type ResearchAnswer = (typeof RESEARCH_ANSWERS)[number];

/** One state's answer for one category, as `category-answers.json` holds it. */
export interface CategoryAnswerEntry {
  answer: ResearchAnswer;
  condition: string | null;
  ratePercent: number | null;
  /** The day the current answer started, where it changed after 2025-01-01; null where it held all along. */
  effectiveFrom: string | null;
  /** The answer that held from 2025-01-01 until `effectiveFrom`, where the rule changed in that window. */
  history: { answer: ResearchAnswer; ratePercent: number | null; from: string }[];
  source: string;
  /** The state's own words on the source, or null where none were read (decision 0903). */
  quote: string | null;
  /** True only where the words were read on the state's own site (decision 0903). */
  primary: boolean;
  /** Why an answer is not primary, or what changed it, in the research's words; the seed never reads it (decision 0903). */
  note?: string | null;
}

/** The committed research file: 51 jurisdictions, each with its thirteen answers, its notes and its confidence. */
export type CategoryAnswerFile = Readonly<Record<string, { categories: Readonly<Record<string, CategoryAnswerEntry>>; notes: string | null; confidence: string | null }>>;

/** One row the seed writes, by state and category name. */
export interface SeededRuleRow {
  stateCode: string;
  categoryName: string;
  treatment: TaxTreatmentValue;
  note: string | null;
  ratePercent: number | null;
  source: string;
  lastVerifiedOn: string;
  effectiveFrom: string;
  supersededAt: string | null;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * An answer the file gives, as a row's three columns (decision 0902): Taxed is
 * Taxable, Not taxed NotTaxable, Lower rate Custom with its rate, and
 * Conditional Default with the condition as its note. 🔨 Only a Conditional
 * answer carries its words onto the row: the research wrote notes beside many
 * Taxed and Not taxed answers too, and those stay in the file.
 */
function shapeOf(answer: ResearchAnswer, condition: string | null, ratePercent: number | null, where: string): TaxTreatmentShape {
  switch (answer) {
    case 'Taxed':
      return { treatment: 'Taxable', note: null, ratePercent: null };
    case 'Not taxed':
      return { treatment: 'NotTaxable', note: null, ratePercent: null };
    case 'Conditional':
      if (!condition || condition.trim() === '') throw new Error(`${where}: a Conditional answer names its condition`);
      return { treatment: 'Default', note: condition.trim(), ratePercent: null };
    case 'Lower rate':
      if (ratePercent === null || ratePercentProblem(ratePercent) !== null) throw new Error(`${where}: a Lower rate answer names a rate from 0 to 25 percent`);
      return { treatment: 'Custom', note: null, ratePercent };
    default:
      throw new Error(`${where}: an answer is one of ${RESEARCH_ANSWERS.join(', ')}`);
  }
}

/**
 * 🔒 The rows the seed writes from the committed file (decision 0902), state
 * by state in the file's order and category by category in the list's order.
 *
 * - A pairing whose answer changed after 2025-01-01 takes two rows: the earlier
 *   answer from its own start, superseded on the day the current one starts.
 * - Every other pairing takes one row from 2025-01-01, or from its own start
 *   where that is later.
 * - The five states with no sales tax take none: their rate is 0, none can be
 *   registered, and a row cannot be taken back once written.
 *
 * 🔨 The file is refused whole rather than seeded in part where it names a
 * state that is not one of the fifty-one, misses one of the thirteen, or gives
 * an answer a row cannot hold.
 */
export function seededRuleRows(file: CategoryAnswerFile): SeededRuleRow[] {
  const categories = TAX_CATEGORY_ROWS.filter((row) => row.isActive).map((row) => row.name);
  const rows: SeededRuleRow[] = [];
  for (const stateCode of Object.keys(US_STATE_NAMES)) {
    if (!file[stateCode]) throw new Error(`the category answers file carries no entry for ${stateCode}`);
  }
  for (const [stateCode, entry] of Object.entries(file)) {
    if (!US_STATE_NAMES[stateCode]) throw new Error(`the category answers file names ${stateCode}, which is not one of the fifty states or the District of Columbia`);
    const named = Object.keys(entry.categories ?? {});
    const unknown = named.filter((name) => !categories.includes(name));
    if (unknown.length > 0) throw new Error(`${stateCode}: the category answers file names ${unknown.join(', ')}, which is no active tax category`);
    if (NO_SALES_TAX_STATES.includes(stateCode)) continue;
    for (const categoryName of categories) {
      const answer = entry.categories[categoryName];
      const where = `${stateCode} · ${categoryName}`;
      if (!answer) throw new Error(`${where}: the category answers file carries no answer`);
      const url = answer.source?.trim();
      if (!url || sourceUrlOf(url) === null) throw new Error(`${where}: an answer names the page it was read on`);
      const source = seededRuleSource(url);
      const current = shapeOf(answer.answer, answer.condition, answer.ratePercent, where);
      const history = Array.isArray(answer.history) ? answer.history : [];
      if (answer.effectiveFrom !== null && !ISO_DAY.test(answer.effectiveFrom)) throw new Error(`${where}: the answer's start is a calendar day`);
      if (history.length > 0) {
        if (history.length > 1) throw new Error(`${where}: the file holds one earlier answer at most`);
        const earlier = history[0]!;
        if (!answer.effectiveFrom || !ISO_DAY.test(earlier.from) || earlier.from >= answer.effectiveFrom) throw new Error(`${where}: an earlier answer starts before the current one`);
        // A Conditional answer the file held earlier becomes a Default whose note is the category's own words, which say what changed.
        const shape = shapeOf(earlier.answer, answer.condition, earlier.ratePercent, `${where} (before ${answer.effectiveFrom})`);
        rows.push({ stateCode, categoryName, ...shape, source, lastVerifiedOn: SEEDED_RULE_VERIFIED_ON, effectiveFrom: earlier.from, supersededAt: answer.effectiveFrom });
        rows.push({ stateCode, categoryName, ...current, source, lastVerifiedOn: SEEDED_RULE_VERIFIED_ON, effectiveFrom: answer.effectiveFrom, supersededAt: null });
        continue;
      }
      const from = answer.effectiveFrom && answer.effectiveFrom > SEEDED_RULE_AS_OF ? answer.effectiveFrom : SEEDED_RULE_AS_OF;
      rows.push({ stateCode, categoryName, ...current, source, lastVerifiedOn: SEEDED_RULE_VERIFIED_ON, effectiveFrom: from, supersededAt: null });
    }
  }
  return rows;
}
